import { ActionAfterViewObserver } from '@cian/action-after-viewed-component';
import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { useOfferMessageTooltip } from './provider';
import {
  trackClickMessage,
  trackClickRecommendation,
  trackExpertButtonClick,
  trackOfferMessageTeaserShow,
} from './tracking';
import { retrySendMessageById } from '../../actions/sendMessage/retrySendMessageById';
import { OfferMessage } from '../../components/OfferMessage';
import { CHATBOT_ID } from '../../constants/chatbotId';
import { useObserver } from '../../hooks/useObserver';
import { IOfferForClient1 } from '../../repositories/chats/entities/for_client/OfferForClient1';
import { currentOpenChatIDSelector } from '../../selectors';
import { applyOfferReaction } from '../../thunks/chatbot';
import { setViewOffer } from '../../thunks/chatbot/feedback/setViewOffer';
import { sendChatbotMessage } from '../../thunks/chatbot/sendChatbotMessage';
import { ERequestApiStatus } from '../../types/appState';
import { IChatbotMessageOfferSchema, TChatbotReaction } from '../../types/chatbot';
import { TExpertButtonContent } from '../../types/expertButtonContent';
import { useMessageText } from '../ChatMessageContainer/hooks';
import { trackLike } from '../ChatMessageContainer/tracking';
import { useMessageContext } from '../../utils/messageContext';
import { useRuntimeSetting } from '../../hooks/useRuntimeSetting';

type IBaseOfferSchema = IChatbotMessageOfferSchema & IOfferForClient1;

interface IOfferMessageContainerProps {
  offer?: IOfferForClient1;
}

export const OfferMessageContainer: React.FC<IOfferMessageContainerProps> = ({ offer: offerFromProps }) => {
  const { message, messageId } = useMessageContext();
  const { status, offers, communicationId, isOutcoming } = message;
  const dispatch = useDispatch();

  const isMessageFailed = status === ERequestApiStatus.Failed;

  const isChatbotGPTEnabled = useRuntimeSetting<boolean>('frontend-chats.chatbotGPT.Enabled', false);
  const isHorizontalGalleryOffersViewEnabled = useRuntimeSetting<boolean>(
    'frontend-chats.horizontalGalleryOffers.Enabled',
    false,
  );
  const expertButtonContent = useRuntimeSetting<TExpertButtonContent>('frontend-chats.expertSearchButton');

  const [reaction, setReaction] = React.useState<TChatbotReaction | undefined>(undefined);
  const [isExpertEnabled, setIsExpertEnabled] = React.useState<boolean | undefined>(undefined);
  const chatId = useSelector(currentOpenChatIDSelector);
  const offer = (offerFromProps ?? offers?.[0]) as IBaseOfferSchema;
  const isChatbotChat = Boolean(chatId === CHATBOT_ID);

  const { showTooltip, hideTooltip, offerMessageTooltipState, rootRef } = useOfferMessageTooltip();
  const rootElementRef = React.useRef<HTMLDivElement | null>(null);
  const messageRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (offer) {
      setReaction(offer.reaction ?? undefined);
      setIsExpertEnabled(offer.isExpertEnabled ?? undefined);
    } else {
      setReaction(undefined);
    }
  }, [offer]);

  useObserver({
    rootElementRef,
    targetElementRef: messageRef,
    condition: isChatbotChat,
    handler: () => {
      if (isExpertEnabled && !offerMessageTooltipState?.isVisible && offer && offer.realtyOfferId && !isOutcoming) {
        setTimeout(() => {
          showTooltip(offer.realtyOfferId);
        }, 4000);
      }
    },
  });

  const handleCloseTooltip = () => {
    hideTooltip();
  };

  const handleReload = React.useCallback(() => {
    dispatch(retrySendMessageById(chatId as string, messageId));
  }, [chatId, dispatch, messageId]);

  const handleTrackOfferMessageTeaserShow = React.useCallback(() => {
    if (offer) {
      trackOfferMessageTeaserShow({
        offerId: offer.realtyOfferId,
        block: isChatbotChat ? 'Chat_cian_helper' : 'Chat_snippet',
        mlSearchSessionGuid: offer.mlSearchSessionGuid ?? undefined,
        batchid: offer.batchTs ?? undefined,
        position: offer.position ?? undefined,
      });
    }
  }, [isChatbotChat, offer]);

  const onClick = React.useCallback(() => {
    if (isChatbotChat && offer) {
      dispatch(setViewOffer(offer.realtyOfferId));
      trackClickRecommendation(
        offer.realtyOfferId,
        communicationId,
        offer.isExpertEnabled ?? false,
        isHorizontalGalleryOffersViewEnabled,
        offer.position ?? undefined,
      );
    } else if (offer && chatId) {
      trackClickMessage(offer.realtyOfferId, chatId, messageId, communicationId);
    }
  }, [isChatbotChat, offer]);

  const handleLikeClick = React.useCallback(() => {
    if (!offer) {
      return;
    }

    const nextReaction = reaction !== 'like' ? 'like' : undefined;

    setReaction(nextReaction);
    trackLike(offer.realtyOfferId, nextReaction, communicationId, offer.position ?? undefined);
    dispatch(
      applyOfferReaction({
        offerId: offer.realtyOfferId,
        reaction: nextReaction,
      }),
    );
  }, [dispatch, offer, reaction]);

  const handleExpertButtonClick = () => {
    if (offer) {
      trackExpertButtonClick(offer.realtyOfferId, communicationId, offer.position ?? undefined);
    }
    dispatch(sendChatbotMessage(expertButtonContent?.sendText || '', offer?.realtyOfferId, messageId));
  };

  const messageWithOffer = React.useMemo(() => ({ ...message, offers: [offer] }), [message, offer]);

  const formattedText = useMessageText({ message: messageWithOffer, messageId });

  if (!offer) {
    return null;
  }

  const { formattedAddress, formattedInfo, formattedPrice, formattedOldPrice, url, photoUrls } = offer;

  const param = `source=${isChatbotChat ? 'chat_bot' : 'chats'}`;
  const shareUrl = url.includes('?') ? `${url}&${param}` : `${url}?${param}`;

  return (
    <ActionAfterViewObserver
      percentVisible={0.5}
      callback={handleTrackOfferMessageTeaserShow}
      triggerOnce={false}
      callbackDelay={1000}
    >
      <OfferMessage
        isOutcoming={isOutcoming}
        isChatbotChat={isChatbotChat}
        isExpertButtonShow={isChatbotGPTEnabled && isExpertEnabled}
        reaction={reaction}
        formattedAddress={formattedAddress}
        formattedInfo={formattedInfo}
        formattedPrice={formattedPrice}
        formattedOldPrice={formattedOldPrice}
        formattedText={formattedText}
        shareUrl={shareUrl}
        photoUrl={photoUrls}
        expertButtonContent={expertButtonContent}
        withReload={isMessageFailed}
        messageRef={messageRef}
        visibleTooltip={offerMessageTooltipState?.offerId === offer.realtyOfferId}
        tooltipPortal={rootRef}
        onReload={handleReload}
        onClick={onClick}
        onLike={handleLikeClick}
        onExpertButton={handleExpertButtonClick}
        setCloseTooltip={handleCloseTooltip}
      />
    </ActionAfterViewObserver>
  );
};
